import React, { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, ChevronLeft, ChevronRight, Gamepad2, BarChart3, Trophy, Globe, Flag, MapPin } from 'lucide-react'
import { useTranslation } from '../hooks/use-i18n'
import { formatPoints } from '../utils/points-label'
import { useSelector } from 'react-redux'
import { RootState } from '../store'
import toast from 'react-hot-toast'
import {
  getActivePublicTournaments,
  getMyPublicEnrollments,
  getMyUpcomingMatches,
  getPublicLeaderboard,
  getPublicLeaderboardCombined,
  getPublicPlayerPredictions,
  submitPublicPrediction,
  QUERY_KEY_MY_PUBLIC_ENROLLMENTS,
  QUERY_KEY_PUBLIC_TOURNAMENTS,
} from '../services/publicService'
import EditPublicLocationModal from '../components/EditPublicLocationModal'
import LeaveWorldFootballModal from '../components/LeaveWorldFootballModal'
import {
  asPublicEnrollmentList,
  findActiveFootballParticipant,
  findPublicLocationFromEnrollments,
  getFootballTournamentIdsFromParticipant,
} from '../utils/publicFootballEnrollment'
import { syncWorldEnrollCache } from '../utils/worldEnrollCache'
import type { LeaderboardScope } from '../services/publicService'
import type { PublicTournament, PublicParticipant, LeaderboardEntry, PublicPlayerPredictionItem } from '../types/public'
import type { UpcomingMatchItem } from '../services/publicService'
import { ScoreEnum, PredictionTypeEnum, getValueByPredictionType } from '../types/prediction'
import { getMatchDate, formatDate } from '../utils/dateHelper'
import SelectResultModal from '../components/SelectResultModal'
import ExistedPredictionModal from '../components/ExistedPredictionModal'
import PublicPlayerStatsGamesModal from '../components/PublicPlayerStatsGamesModal'
import PublicProfileModal from '../components/PublicProfileModal'
import TeamNameWithFlag from '../components/TeamNameWithFlag'
import VictoryTeamLabel from '../components/VictoryTeamLabel'
import LeagueBadge from '../components/LeagueBadge'
import MatchTeamsInline from '../components/MatchTeamsInline'
import OnboardingConciergeModal from '../components/agent/OnboardingConciergeModal'
import InvitePlayerModal from '../components/InvitePlayerModal'
import PlayPublicModal from '../components/PlayPublicModal'
import { footballDisplayScore, footballLiveBadge, FOOTBALL_LIVE_POLL_MS, isFootballMatchLive } from '../utils/footballLiveScore'

type TabId = 'play' | 'results' | 'rankings'

/** Display only the tournament/league name without "Public Football – " prefix. */
function leagueDisplayName(name: string): string {
  if (!name) return name
  const prefix = 'Public Football – '
  const prefixAlt = 'Public Football - '
  if (name.startsWith(prefix)) return name.slice(prefix.length).trim()
  if (name.startsWith(prefixAlt)) return name.slice(prefixAlt.length).trim()
  return name
}

/**
 * Same scoring logic as the public leaderboard but returns the winner / score split so the
 * room-style breakdown ("Result" / "Score") can be displayed. Prefers backend-provided
 * point fields when present; otherwise mirrors computePoints exactly.
 */
function computePointsSplit(item: PublicPlayerPredictionItem): { winnerPoint: number; scorePoint: number } {
  const { prediction, match } = item
  if (prediction.predictedWinnerPoint != null || prediction.predictedScorePoint != null) {
    return {
      winnerPoint: Number(prediction.predictedWinnerPoint) || 0,
      scorePoint: Number(prediction.predictedScorePoint) || 0,
    }
  }
  if (!match?.score || match.winner === undefined) return { winnerPoint: 0, scorePoint: 0 }
  const predictedWinner = prediction.winner ? String(prediction.winner).trim() : null
  const actualWinner = match.winner ? String(match.winner).trim() : null
  const winnerCorrect = predictedWinner === actualWinner
  let winnerPoint = 0
  if (winnerCorrect) winnerPoint = match.winner ? 1 : 2
  const predScoreStr = String(prediction.predictedScore || '').trim()
  const matchScoreStr = String(match.score || '').trim()
  let scorePoint = 0
  if (predScoreStr && matchScoreStr && predScoreStr === matchScoreStr && winnerCorrect) {
    const rules: Record<string, number> = {
      '1-0': 1, '2-0': 1, '2-1': 1, '0-0': 2, '1-1': 2, '2-2': 2,
      '3-0': 2, '3-1': 2, '3-2': 2, '3-3': 3, '4-0': 3, '4-1': 3, '4-2': 3, '4-3': 3, '4-4': 4,
    }
    scorePoint = rules[predScoreStr] ?? 1
  }
  return { winnerPoint, scorePoint }
}

/** Room-style "(Team wins)" / "(Draw)" suffix for a predicted/actual winner. */
function buildWinnerSuffix(winner: string | null | undefined, t: (k: string, o?: any) => string): string {
  const w = winner ? String(winner).trim() : ''
  if (!w || w === 'null') return ` (${t('stats.draw')})`
  return ` (${t('stats.teamWins', { team: w })})`
}

const PublicFootballPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab') as TabId | null
  const initialTab: TabId = tabParam === 'play' || tabParam === 'results' || tabParam === 'rankings' ? tabParam : 'rankings'
  const [activeTab, setActiveTab] = useState<TabId>(initialTab)

  const { t } = useTranslation()
  const { user: currentUser } = useSelector((state: RootState) => state.auth)
  const currentUserId = (currentUser as any)?._id ?? (currentUser as any)?.id ?? ''

  const [showEditPreferencesModal, setShowEditPreferencesModal] = useState(false)
  const [showLeaveWorldModal, setShowLeaveWorldModal] = useState(false)
  const [showConciergeModal, setShowConciergeModal] = useState(false)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [showPlayPublicModal, setShowPlayPublicModal] = useState(false)
  const queryClient = useQueryClient()

  // When entering public football, always jump to top so the main dashboard header is visible
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // React Query: shared cache with Dashboard so revisiting is instant
  const { data: leaguesData = [] } = useQuery({
    queryKey: [QUERY_KEY_PUBLIC_TOURNAMENTS, 'football'],
    queryFn: () => getActivePublicTournaments('football'),
    staleTime: 2 * 60 * 1000,
  })
  const { data: enrollmentsData = [], isLoading: loadingEnrollments } = useQuery({
    queryKey: QUERY_KEY_MY_PUBLIC_ENROLLMENTS,
    queryFn: getMyPublicEnrollments,
    staleTime: 0,
  })

  const enrollments = asPublicEnrollmentList(enrollmentsData)
  const firstEnrollment = findActiveFootballParticipant(enrollments)
  const enrolledFootballTournamentIds = getFootballTournamentIdsFromParticipant(firstEnrollment)
  const enrolledIdsSet = enrolledFootballTournamentIds.length > 0 ? new Set(enrolledFootballTournamentIds) : null
  const listedLeagues = Array.isArray(leaguesData) ? leaguesData : []
  const leaguesFromEnrollment = (firstEnrollment?.participantTournaments ?? [])
    .map((pt) => pt.publicTournament)
    .filter((t): t is PublicTournament => Boolean(t))
  const leaguesFromList = enrolledIdsSet
    ? listedLeagues.filter((l) => enrolledIdsSet.has(l.id))
    : listedLeagues
  const leagues = firstEnrollment
    ? (leaguesFromList.length > 0 ? leaguesFromList : leaguesFromEnrollment)
    : []
  const isEnrolledInWorld = Boolean(firstEnrollment)

  const refetchEnrollments = async (savedLeagueIds?: string[]) => {
    if (savedLeagueIds && firstEnrollment) {
      queryClient.setQueryData(QUERY_KEY_MY_PUBLIC_ENROLLMENTS, (old: PublicParticipant[] | undefined) => {
        const list = old ?? []
        return list.map((p) =>
          p.id === firstEnrollment.id
            ? {
                ...p,
                favoriteTournamentIds: savedLeagueIds,
                participantTournaments: savedLeagueIds.map((id) => ({
                  publicParticipantId: p.id,
                  publicTournamentId: id,
                })),
              }
            : p,
        )
      })
    }
    await queryClient.refetchQueries({ queryKey: [QUERY_KEY_PUBLIC_TOURNAMENTS, 'football'] })
    await queryClient.refetchQueries({ queryKey: QUERY_KEY_MY_PUBLIC_ENROLLMENTS })
    if (savedLeagueIds && firstEnrollment) {
      const after = queryClient.getQueryData<PublicParticipant[]>(QUERY_KEY_MY_PUBLIC_ENROLLMENTS)
      const current = findActiveFootballParticipant(after ?? [])
      const ids = getFootballTournamentIdsFromParticipant(current)
      const same = ids.length === savedLeagueIds.length && savedLeagueIds.every((id) => ids.includes(id))
      if (!same) {
        queryClient.setQueryData(QUERY_KEY_MY_PUBLIC_ENROLLMENTS, (old: PublicParticipant[] | undefined) => {
          const list = old ?? []
          return list.map((p) =>
            p.id === firstEnrollment.id
              ? {
                  ...p,
                  favoriteTournamentIds: savedLeagueIds,
                  participantTournaments: savedLeagueIds.map((id) => ({
                    publicParticipantId: p.id,
                    publicTournamentId: id,
                  })),
                }
              : p,
          )
        })
      }
    }
    await queryClient.invalidateQueries({ queryKey: ['public-upcoming-matches'] })
    await queryClient.refetchQueries({ queryKey: ['public-upcoming-matches'] })
  }

  // Sync tab from URL
  useEffect(() => {
    const t = searchParams.get('tab') as TabId | null
    if (t === 'play' || t === 'results' || t === 'rankings') setActiveTab(t)
  }, [searchParams])

  const setTab = (tab: TabId) => {
    setActiveTab(tab)
    setSearchParams({ tab })
  }

  const hasFootballEnrollment = isEnrolledInWorld
  const existingLocation = findPublicLocationFromEnrollments(enrollments)
  const scopeParticipant = firstEnrollment
    ? { countryCode: firstEnrollment.countryCode, city: firstEnrollment.city ?? undefined, continentCode: firstEnrollment.continentCode }
    : null

  if (loadingEnrollments && !hasFootballEnrollment) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    )
  }

  if (!hasFootballEnrollment) {
    return (
      <div className="min-h-screen bg-background">
        <main className="pt-16 container mx-auto px-4 pb-8">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6">
            <ArrowLeft size={18} /> {t('dashboard.backToDashboard')}
          </Link>
          <div className="glass-card rounded-2xl p-8 text-center max-w-md mx-auto">
            <p className="text-foreground mb-4">{t('publicFootball.notEnrolled')}</p>
            <button
              type="button"
              onClick={() => setShowPlayPublicModal(true)}
              className="btn-primary px-4 py-2 rounded-xl w-full mb-2"
            >
              ⚽ {t('dashboard.playWorldFootball')}
            </button>
            <button
              type="button"
              onClick={() => setShowConciergeModal(true)}
              className="px-4 py-2 rounded-xl w-full mb-2 border border-primary/40 text-foreground"
            >
              ✨ {t('agent.askConcierge')}
            </button>
            <Link to="/dashboard" className="inline-block text-sm text-muted-foreground hover:text-foreground">
              {t('publicFootball.goToDashboard')}
            </Link>
          </div>
        </main>
        {showPlayPublicModal && (
          <PlayPublicModal
            onlyGameType="football"
            initialCountryCode={existingLocation?.countryCode}
            initialCity={existingLocation?.city}
            onClose={() => setShowPlayPublicModal(false)}
            onSuccess={async (_tournamentId, gameType, extras) => {
              setShowPlayPublicModal(false)
              await syncWorldEnrollCache(queryClient, gameType ?? 'football', extras)
            }}
          />
        )}
        {showConciergeModal && (
          <OnboardingConciergeModal
            onlyGameType="football"
            onClose={() => setShowConciergeModal(false)}
            onEnrolled={async (gameType, extras) => {
              setShowConciergeModal(false)
              await syncWorldEnrollCache(queryClient, gameType, extras)
            }}
          />
        )}
      </div>
    )
  }

  const tabs: { id: TabId; label: string }[] = [
    { id: 'play', label: t('publicFootball.tabPlayUpcoming') },
    { id: 'results', label: t('publicFootball.tabMyResults') },
    { id: 'rankings', label: t('publicFootball.tabMyRankings') },
  ]

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-16 container mx-auto px-4 pb-6 max-w-4xl">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft size={16} /> {t('dashboard.backToDashboard')}
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            {isEnrolledInWorld && (
              <button
                type="button"
                onClick={() => setShowLeaveWorldModal(true)}
                className="text-xs text-destructive/90 hover:text-destructive px-2.5 py-1 rounded-lg bg-destructive/10"
              >
                {t('worldFootball.leave.action')}
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowInviteModal(true)}
              className="text-xs text-foreground hover:text-primary px-2.5 py-1 rounded-lg bg-primary/10"
            >
              {t('invitations.inviteFriend')}
            </button>
            <button
              type="button"
              onClick={() => setShowEditPreferencesModal(true)}
              className="text-xs text-muted-foreground hover:text-foreground px-2.5 py-1 rounded-lg bg-muted/50"
            >
              {t('publicFootball.editPreferences')}
            </button>
          </div>
        </div>
        <h1 className="font-display text-lg sm:text-xl font-bold text-foreground mb-4">
          {t('publicFootball.pageTitle')}
        </h1>
        <EditPublicLocationModal
          isOpen={showEditPreferencesModal}
          onClose={() => setShowEditPreferencesModal(false)}
          participant={firstEnrollment ?? null}
          leagues={leaguesData}
          enrolledTournamentIds={enrolledFootballTournamentIds}
          onSaved={refetchEnrollments}
        />
        {showLeaveWorldModal && (
          <LeaveWorldFootballModal
            onClose={() => setShowLeaveWorldModal(false)}
            onSuccess={refetchEnrollments}
          />
        )}
        {showInviteModal && (
          <InvitePlayerModal
            kind="world_football"
            onClose={() => setShowInviteModal(false)}
            onSuccess={() => setShowInviteModal(false)}
          />
        )}

        <div className="flex flex-wrap gap-2 sm:gap-1.5 mb-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setTab(tab.id)}
              className={`px-4 py-2 sm:px-3 sm:py-1.5 rounded-xl text-xs font-medium transition-all ${
                activeTab === tab.id ? 'bg-primary text-primary-foreground' : 'bg-muted/20 text-foreground hover:bg-muted/40'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'play' && (
          <PlayUpcomingTab
            key={`play-${enrolledFootballTournamentIds.join(',')}-${(firstEnrollment?.favoriteTournamentIds ?? []).join(',')}`}
            leagues={leagues}
            currentUserId={currentUserId}
            defaultLeagueIds={(() => {
              const ids = firstEnrollment?.favoriteTournamentIds
              if (!ids?.length) return null
              const valid = ids.filter((id) => leagues.some((l) => l.id === id))
              return valid.length > 0 ? valid : null
            })()}
            onAllPredictionsComplete={() => setTab('rankings')}
          />
        )}
        {activeTab === 'results' && (
          <MyResultsTab
            key={`results-${(firstEnrollment?.favoriteTournamentIds ?? []).join(',')}`}
            leagues={leagues}
            currentUserId={currentUserId}
            defaultLeagueIds={(() => {
              const ids = firstEnrollment?.favoriteTournamentIds
              if (!ids?.length) return null
              const valid = ids.filter((id) => leagues.some((l) => l.id === id))
              return valid.length > 0 ? valid : null
            })()}
          />
        )}
        {activeTab === 'rankings' && (
          <MyRankingsTab
            key={`rankings-${(firstEnrollment?.favoriteTournamentIds ?? []).join(',')}`}
            leagues={leagues}
            scopeParticipant={scopeParticipant}
            defaultLeagueIds={(() => {
              const ids = firstEnrollment?.favoriteTournamentIds
              if (!ids?.length) return null
              const valid = ids.filter((id) => leagues.some((l) => l.id === id))
              return valid.length > 0 ? valid : null
            })()}
            currentUserId={currentUserId}
          />
        )}
      </main>
    </div>
  )
}

function PlayUpcomingTab({
  leagues,
  currentUserId,
  defaultLeagueIds = null,
  onAllPredictionsComplete,
}: {
  leagues: PublicTournament[]
  currentUserId: string
  defaultLeagueIds?: string[] | null
  onAllPredictionsComplete?: () => void
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const hasDefaults = defaultLeagueIds && defaultLeagueIds.length > 0
  const [allLeaguesSelected, setAllLeaguesSelected] = useState(!hasDefaults)
  const [selectedIds, setSelectedIds] = useState<string[]>(hasDefaults ? [...defaultLeagueIds] : [])
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [selectedType, setSelectedType] = useState<PredictionTypeEnum>(PredictionTypeEnum.Win)
  const [selectedWinnerTeam, setSelectedWinnerTeam] = useState<string | null>(null)
  const [isOpenResultModal, setIsOpenResultModal] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [editingMatchId, setEditingMatchId] = useState<string | null>(null)

  const effectiveIds = allLeaguesSelected ? leagues.map((l) => l.id) : selectedIds
  const hasEffectiveLeagues = effectiveIds.length > 0

  const { data: upcomingData, isLoading: loading } = useQuery({
    queryKey: ['public-upcoming-matches'],
    queryFn: () => getMyUpcomingMatches(),
    staleTime: 30 * 1000,
    refetchInterval: FOOTBALL_LIVE_POLL_MS,
  })

  const { data: myPredictionsData } = useQuery({
    queryKey: ['public-my-predictions', currentUserId, [...effectiveIds].sort().join(',')],
    queryFn: () => getPublicPlayerPredictions(currentUserId, effectiveIds),
    enabled: hasEffectiveLeagues && !!currentUserId,
    staleTime: 30 * 1000,
    refetchInterval: FOOTBALL_LIVE_POLL_MS,
  })

  const existingPredictionByMatchId = useMemo(() => {
    const list = myPredictionsData ?? []
    const map: Record<string, PublicPlayerPredictionItem> = {}
    list.forEach((item) => { map[item.match.id] = item })
    return map
  }, [myPredictionsData])

  const items: UpcomingMatchItem[] = allLeaguesSelected
    ? (upcomingData ?? [])
    : !hasEffectiveLeagues
      ? (upcomingData ?? [])
      : (upcomingData ?? []).filter((item: UpcomingMatchItem) => effectiveIds.includes(item.publicTournamentId))

  const now = Date.now()
  const upcoming = items.filter((item: UpcomingMatchItem) => {
    const kickoff = new Date(getMatchDate(item.match, true)).getTime()
    const noResult = !item.match.score || item.match.winner === undefined
    return kickoff > now && noResult
  })

  const totalSteps = upcoming.length
  const currentItem: UpcomingMatchItem | null = totalSteps > 0 && selectedIndex < totalSteps ? upcoming[selectedIndex] : null
  const existingForCurrent = currentItem ? existingPredictionByMatchId[currentItem.match.id] : null
  const showAlreadyPlayed = !!existingForCurrent && editingMatchId !== currentItem?.match?.id

  useEffect(() => {
    setEditingMatchId(null)
  }, [selectedIndex])

  const toggleLeague = (id: string) => {
    setAllLeaguesSelected(false)
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
    setSelectedIndex(0)
  }

  const handleOpenResult = (type: PredictionTypeEnum, team: string | null) => {
    setSelectedType(type)
    setSelectedWinnerTeam(team)
    setIsOpenResultModal(true)
  }

  const handleResultSelect = async (score: ScoreEnum) => {
    if (!currentItem) return
    // Draw: selectedWinnerTeam is null; Win: selectedWinnerTeam is teamA or teamB
    let scoreToSend: string = score
    if (selectedWinnerTeam === currentItem.match.teamB && score !== ScoreEnum.FiveAndMore && score !== ScoreEnum.FiveFiveMore) {
      const [a, b] = score.split('-')
      scoreToSend = `${b}-${a}`
    }
    const winnerTeam =
      selectedWinnerTeam === currentItem.match.teamA
        ? currentItem.match.teamA
        : selectedWinnerTeam === currentItem.match.teamB
          ? currentItem.match.teamB
          : null
    setSubmitting(true)
    setIsOpenResultModal(false)
    const nextIndex = Math.min(selectedIndex + 1, totalSteps - 1)
    try {
      await submitPublicPrediction(currentItem.publicTournamentId, {
        publicParticipantId: currentItem.publicParticipantId,
        matchId: currentItem.match.id,
        winner: winnerTeam ?? undefined,
        predictedScore: scoreToSend,
      })
      toast.success(t('publicHub.predictionSaved'))
      setEditingMatchId(null)
      setSelectedIndex(nextIndex)
      if (selectedIndex === totalSteps - 1 && totalSteps > 0 && onAllPredictionsComplete) {
        onAllPredictionsComplete()
      }
      // Defer cache invalidation to avoid re-render shake right after modal close
      requestAnimationFrame(() => {
        queryClient.invalidateQueries({ queryKey: ['public-upcoming-matches'] })
        queryClient.invalidateQueries({ queryKey: ['public-my-predictions'] })
      })
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('playPublic.loadError'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <p className="text-xs text-muted-foreground mb-2">{t('publicFootball.playUpcomingHint')}</p>
      <label className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-muted/20 cursor-pointer mb-2">
        <input
          type="checkbox"
          checked={allLeaguesSelected}
          onChange={() => { setAllLeaguesSelected((v) => !v); setSelectedIndex(0) }}
          className="rounded"
        />
        <span className="text-xs font-medium">{t('publicFootball.allLeagues')}</span>
      </label>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {leagues.map((l) => (
          <label key={l.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-muted/20 cursor-pointer">
            <input
              type="checkbox"
              checked={allLeaguesSelected || selectedIds.includes(l.id)}
              onChange={() => {
                if (allLeaguesSelected) {
                  setAllLeaguesSelected(false)
                  setSelectedIds([l.id])
                  setSelectedIndex(0)
                } else {
                  toggleLeague(l.id)
                }
              }}
              className="rounded"
            />
            <span className="text-xs font-medium">{leagueDisplayName(l.name)}</span>
          </label>
        ))}
      </div>
      {!hasEffectiveLeagues ? (
        <p className="text-muted-foreground py-4 text-sm">{t('publicFootball.selectLeagues')}</p>
      ) : loading ? (
        <div className="flex justify-center py-6"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" /></div>
      ) : totalSteps === 0 ? (
        <p className="text-muted-foreground py-4 text-sm">{t('publicHub.noUpcomingMatches')}</p>
      ) : currentItem ? (
        <div className="mx-auto max-w-md">
          <div className="blue-sky-card rounded-xl p-4 sm:p-5 text-center">
            <div className="flex items-center justify-between w-full mb-3">
              <div className="flex gap-1">
                {Array.from({ length: Math.min(totalSteps, 12) }).map((_, i) => (
                  <div
                    key={i}
                    className={`h-1.5 w-3 sm:w-4 rounded-sm flex-shrink-0 ${i <= selectedIndex ? 'bg-primary' : 'bg-white/20'}`}
                  />
                ))}
              </div>
              <span className="text-white/80 font-semibold text-xs ml-1">{selectedIndex + 1}/{totalSteps}</span>
            </div>
            <div className="mb-2">
              <div className="mb-1 flex justify-center">
                <LeagueBadge league={currentItem.match.league} size={36} />
              </div>
              <h2 className="text-base sm:text-lg font-bold text-white">
                {currentItem.match.number != null && currentItem.match.number !== ''
                  ? currentItem.match.number
                  : t('publicFootball.title')}
              </h2>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-1.5 mb-3">
              <span className="text-white/80 text-xs">{formatDate(getMatchDate(currentItem.match, true), true)}</span>
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary/30 text-white">{leagueDisplayName(currentItem.tournamentName)}</span>
            </div>
            <div className="flex justify-between items-center mb-4 gap-3">
              <div className="flex-1 text-center min-w-0">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-1">
                  <span className="text-lg sm:text-xl">🏠</span>
                </div>
                <h3 className="text-sm sm:text-base font-semibold text-white">
                  <TeamNameWithFlag
                    teamName={currentItem.match.teamA}
                    league={currentItem.match.league}
                    side="home"
                    nameClassName="text-sm sm:text-base font-semibold text-white"
                  />
                </h3>
              </div>
              <div className="text-lg sm:text-xl font-bold text-white flex-shrink-0">VS</div>
              <div className="flex-1 text-center min-w-0">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-1">
                  <span className="text-lg sm:text-xl">✈️</span>
                </div>
                <h3 className="text-sm sm:text-base font-semibold text-white">
                  <TeamNameWithFlag
                    teamName={currentItem.match.teamB}
                    league={currentItem.match.league}
                    side="away"
                    nameClassName="text-sm sm:text-base font-semibold text-white"
                  />
                </h3>
              </div>
            </div>
            <div className={`space-y-2 sm:space-y-2.5 ${showAlreadyPlayed ? 'opacity-45 pointer-events-none' : ''}`}>
              <button
                onClick={() => handleOpenResult(PredictionTypeEnum.Win, currentItem.match.teamA)}
                disabled={submitting || showAlreadyPlayed}
                className="w-full px-4 py-2.5 sm:py-3 bg-primary hover:opacity-90 text-primary-foreground rounded-lg text-sm font-semibold disabled:opacity-50"
              >
                <VictoryTeamLabel
                  teamName={currentItem.match.teamA}
                  league={currentItem.match.league}
                  side="home"
                />
              </button>
              <button
                onClick={() => handleOpenResult(PredictionTypeEnum.Draw, null)}
                disabled={submitting || showAlreadyPlayed}
                className="w-full px-4 py-2.5 sm:py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
              >
                {t('predictions.draw')}
              </button>
              <button
                onClick={() => handleOpenResult(PredictionTypeEnum.Win, currentItem.match.teamB)}
                disabled={submitting || showAlreadyPlayed}
                className="w-full px-4 py-2.5 sm:py-3 bg-primary hover:opacity-90 text-primary-foreground rounded-lg text-sm font-semibold disabled:opacity-50"
              >
                <VictoryTeamLabel
                  teamName={currentItem.match.teamB}
                  league={currentItem.match.league}
                  side="away"
                />
              </button>
            </div>
            <div className="flex items-center justify-center gap-3 mt-4">
              <button
                type="button"
                onClick={() => setSelectedIndex((i) => Math.max(0, i - 1))}
                disabled={selectedIndex === 0}
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40 disabled:pointer-events-none flex items-center justify-center text-white"
                aria-label="Previous"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setSelectedIndex((i) => Math.min(totalSteps - 1, i + 1))}
                disabled={selectedIndex >= totalSteps - 1}
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40 disabled:pointer-events-none flex items-center justify-center text-white"
                aria-label="Next"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          {showAlreadyPlayed && existingForCurrent && currentItem ? (
            <ExistedPredictionModal
              existedPrediction={{
                _id: existingForCurrent.prediction.id,
                winner: existingForCurrent.prediction.winner,
                predictedScore: existingForCurrent.prediction.predictedScore,
                match: existingForCurrent.match.id,
                player: currentUserId,
                room: '',
                predictedWinnerPoint: existingForCurrent.prediction.predictedWinnerPoint ?? null,
                predictedScorePoint: existingForCurrent.prediction.predictedScorePoint ?? null,
                createdAt: '',
                updatedAt: '',
              }}
              match={{
                _id: currentItem.match.id,
                index: '',
                number: currentItem.match.number ?? undefined,
                teamA: currentItem.match.teamA,
                teamB: currentItem.match.teamB,
                date: currentItem.match.date,
                time: currentItem.match.time,
                winner: currentItem.match.winner,
                score: currentItem.match.score ?? '',
                league: currentItem.match.league,
              }}
              onKeepClick={() => setSelectedIndex((i) => Math.min(totalSteps - 1, i + 1))}
              onChangeClick={() => setEditingMatchId(currentItem.match.id)}
            />
          ) : null}
          <SelectResultModal
            isOpen={isOpenResultModal}
            title={`${currentItem.match.teamA} - ${currentItem.match.teamB}`}
            type={selectedType}
            variant={selectedWinnerTeam === currentItem.match.teamA ? 'a' : 'b'}
            onClose={() => setIsOpenResultModal(false)}
            onSelect={handleResultSelect}
          />
        </div>
      ) : null}
    </div>
  )
}

function MyResultsTab({
  leagues,
  currentUserId,
  defaultLeagueIds = null,
}: {
  leagues: PublicTournament[]
  currentUserId: string
  defaultLeagueIds?: string[] | null
}) {
  const { t } = useTranslation()
  const hasDefaults = defaultLeagueIds && defaultLeagueIds.length > 0
  const [allLeaguesSelected, setAllLeaguesSelected] = useState(!hasDefaults)
  const [selectedIds, setSelectedIds] = useState<string[]>(hasDefaults ? [...defaultLeagueIds] : [])
  const [items, setItems] = useState<PublicPlayerPredictionItem[]>([])
  const [loading, setLoading] = useState(false)

  const effectiveIds = allLeaguesSelected ? leagues.map((l) => l.id) : selectedIds
  const hasEffectiveLeagues = effectiveIds.length > 0

  useEffect(() => {
    if (!hasEffectiveLeagues || !currentUserId) {
      setItems([])
      return
    }
    let cancelled = false
    setLoading(true)
    getPublicPlayerPredictions(currentUserId, effectiveIds)
      .then((list) => {
        if (cancelled) return
        setItems(list.filter((i) => i.match?.score != null && i.match.winner !== undefined))
      })
      .catch(() => { if (!cancelled) setItems([]) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [effectiveIds.join(','), currentUserId, hasEffectiveLeagues])

  const toggleLeague = (id: string) => {
    setAllLeaguesSelected(false)
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  const sorted = [...items].sort((a, b) => new Date(getMatchDate(b.match, true)).getTime() - new Date(getMatchDate(a.match, true)).getTime())

  return (
    <div>
      <p className="text-xs text-muted-foreground mb-2">{t('publicFootball.myResultsHint')}</p>
      <label className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-muted/20 cursor-pointer mb-2">
        <input
          type="checkbox"
          checked={allLeaguesSelected}
          onChange={() => setAllLeaguesSelected((v) => !v)}
          className="rounded"
        />
        <span className="text-xs font-medium">{t('publicFootball.allLeagues')}</span>
      </label>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {leagues.map((l) => (
          <label key={l.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-muted/20 cursor-pointer">
            <input
              type="checkbox"
              checked={allLeaguesSelected || selectedIds.includes(l.id)}
              onChange={() => {
                if (allLeaguesSelected) {
                  setAllLeaguesSelected(false)
                  setSelectedIds([l.id])
                } else {
                  toggleLeague(l.id)
                }
              }}
              className="rounded"
            />
            <span className="text-xs font-medium">{leagueDisplayName(l.name)}</span>
          </label>
        ))}
      </div>
      {!hasEffectiveLeagues ? (
        <p className="text-muted-foreground py-6">{t('publicFootball.selectLeagues')}</p>
      ) : loading ? (
        <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
      ) : sorted.length === 0 ? (
        <p className="text-muted-foreground py-6">{t('publicFootball.noResultsYet')}</p>
      ) : (
        <div className="space-y-3 sm:space-y-4">
          {sorted.map((item) => {
            const { winnerPoint, scorePoint } = computePointsSplit(item)
            const totalPoints = winnerPoint + scorePoint
            const matchScoreStr = footballDisplayScore(item.match)
            const liveBadge = footballLiveBadge(item.match)
            return (
              <div key={item.prediction.id} className="glass-card rounded-lg sm:rounded-xl p-3 sm:p-4">
                <div className="space-y-1.5 sm:space-y-2">
                  <div className="flex items-start justify-between gap-2 sm:gap-3">
                    <div className="flex items-center flex-wrap gap-2 min-w-0 flex-1">
                      <MatchTeamsInline
                        teamA={item.match.teamA}
                        teamB={item.match.teamB}
                        league={item.match.league}
                        separator="-"
                        nameClassName="font-semibold text-small sm:text-body text-foreground"
                      />
                      {matchScoreStr !== '' && (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-lg font-semibold text-xs sm:text-small border shrink-0 ${isFootballMatchLive(item.match) ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-primary/20 text-primary border-primary/30'}`}>
                          {liveBadge ? `${liveBadge} · ` : ''}{t('roomPage.realScore')}: {matchScoreStr}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground shrink-0 text-right whitespace-nowrap pt-0.5 flex items-center gap-1 justify-end">
                      <LeagueBadge league={item.match.league} size={16} />
                      {leagueDisplayName(item.tournamentName)}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">{formatDate(getMatchDate(item.match), true)}</div>
                  <div className="text-primary font-medium text-small sm:text-body">
                    {t('publicFootball.myPrediction')}: {item.prediction.predictedScore}{buildWinnerSuffix(item.prediction.winner, t)}
                  </div>
                  <div className="mt-2 sm:mt-3 flex flex-wrap items-center gap-3 sm:gap-4 text-small sm:text-body">
                    <span className="text-muted-foreground">
                      {t('stats.resultLabel')}:{' '}
                      <span className="font-semibold text-foreground">{winnerPoint}</span>
                    </span>
                    <span className="text-muted-foreground">
                      {t('stats.scoreLabel')}:{' '}
                      <span className="font-semibold text-foreground">{scorePoint}</span>
                    </span>
                    <span
                      className={`ml-auto inline-flex items-center px-3 py-1 rounded-lg font-bold ${
                        totalPoints === 0 ? 'bg-destructive/20 text-destructive' : 'bg-success/20 text-success'
                      }`}
                    >
                      {formatPoints(totalPoints, t)}
                    </span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function rankMedal(rank: number) {
  if (rank === 1) return '🥇'
  if (rank === 2) return '🥈'
  if (rank === 3) return '🥉'
  return null
}

function MyRankingsTab({
  leagues,
  scopeParticipant,
  defaultLeagueIds = null,
  currentUserId = '',
}: {
  leagues: PublicTournament[]
  scopeParticipant: { countryCode?: string; city?: string } | null
  defaultLeagueIds?: string[] | null
  currentUserId?: string
}) {
  const { t } = useTranslation()
  const hasDefaults = defaultLeagueIds && defaultLeagueIds.length > 0
  const [allLeaguesSelected, setAllLeaguesSelected] = useState(!hasDefaults)
  const [selectedId, setSelectedId] = useState<string | null>(hasDefaults ? defaultLeagueIds![0] : null)
  const [scope, setScope] = useState<LeaderboardScope>('world')
  const [playerModalEntry, setPlayerModalEntry] = useState<LeaderboardEntry | null>(null)
  const [playerModalViewType, setPlayerModalViewType] = useState<'games' | 'stats'>('games')
  const [profileModalEntry, setProfileModalEntry] = useState<LeaderboardEntry | null>(null)

  const effectiveIds = allLeaguesSelected ? leagues.map((l) => l.id) : selectedId ? [selectedId] : []
  const hasEffectiveLeagues = effectiveIds.length > 0

  const filters =
    scope === 'country' && scopeParticipant?.countryCode
      ? { countryCode: scopeParticipant.countryCode }
      : scope === 'city' && scopeParticipant?.city
        ? { city: scopeParticipant.city }
        : scope === 'continent' && (scopeParticipant as any)?.continentCode
          ? { continentCode: (scopeParticipant as any).continentCode }
          : undefined

  const {
    data: entries = [],
    isLoading: loading,
    isError,
    refetch,
  } = useQuery({
    queryKey: [
      'public-leaderboard',
      allLeaguesSelected ? 'combined' : 'single',
      effectiveIds.join(','),
      selectedId,
      scope,
      filters?.countryCode,
      filters?.city,
      filters?.continentCode,
    ],
    queryFn: () =>
      allLeaguesSelected
        ? getPublicLeaderboardCombined(effectiveIds, scope, filters)
        : getPublicLeaderboard(selectedId!, scope, filters),
    enabled: hasEffectiveLeagues,
    staleTime: 30 * 1000,
    refetchInterval: FOOTBALL_LIVE_POLL_MS,
    retry: 2,
  })

  const scopes: { key: LeaderboardScope; label: string; Icon: React.ComponentType<{ className?: string }> }[] = [
    { key: 'world', label: t('publicLeaderboard.world'), Icon: Globe },
    { key: 'country', label: t('publicLeaderboard.country'), Icon: Flag },
    { key: 'city', label: t('publicLeaderboard.city'), Icon: MapPin },
  ]

  const scopeTitle =
    scope === 'country' && entries.length > 0
      ? entries[0].countryName
      : scope === 'city' && (entries.length > 0 || scopeParticipant?.city)
        ? (entries[0]?.city ?? scopeParticipant?.city ?? '')
        : null

  const leagueTitle = allLeaguesSelected
    ? t('publicFootball.allLeagues')
    : leagueDisplayName(leagues.find((l) => l.id === selectedId!)?.name ?? '')

  const onToggleAllLeagues = () => {
    setAllLeaguesSelected((v) => !v)
    if (allLeaguesSelected) setSelectedId(null)
  }

  const onSelectLeague = (id: string) => {
    setAllLeaguesSelected(false)
    setSelectedId(id)
  }

  return (
    <div>
      <p className="text-xs sm:text-sm text-muted-foreground mb-3">{t('publicFootball.myRankingsHint')}</p>
      <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/20 cursor-pointer mb-2 min-h-[44px] sm:min-h-0 sm:py-1.5 sm:px-2.5 touch-manipulation">
        <input
          type="checkbox"
          checked={allLeaguesSelected}
          onChange={onToggleAllLeagues}
          className="rounded"
        />
        <span className="text-xs font-medium">{t('publicFootball.allLeagues')}</span>
      </label>
      <div className="flex flex-wrap gap-2 mb-4">
        {leagues.map((l) => (
          <label
            key={l.id}
            className="inline-flex items-center gap-2 px-3 py-2 sm:px-2.5 sm:py-1 rounded-lg bg-muted/20 cursor-pointer touch-manipulation min-h-[44px] sm:min-h-0 sm:py-1.5"
          >
            <input
              type="radio"
              name="rankLeague"
              checked={allLeaguesSelected || selectedId === l.id}
              onChange={() => onSelectLeague(l.id)}
              className="rounded"
            />
            <span className="text-xs font-medium">{leagueDisplayName(l.name)}</span>
          </label>
        ))}
      </div>
      {!hasEffectiveLeagues ? (
        <p className="text-muted-foreground py-4 text-sm">{t('publicFootball.selectOneLeague')}</p>
      ) : (
        <>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
              <Trophy className="w-5 h-5 text-primary" />
            </div>
            <p className="text-sm font-semibold text-foreground truncate">{leagueTitle}</p>
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            {scopes.map((s) => {
              const Icon = s.Icon
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setScope(s.key)}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors min-h-[44px] sm:min-h-0 touch-manipulation ${
                    scope === s.key ? 'bg-primary text-primary-foreground' : 'bg-muted/20 text-foreground hover:bg-muted/30'
                  }`}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  {s.label}
                </button>
              )
            })}
          </div>

          {scope === 'country' && scopeTitle && (
            <div className="flex items-center gap-2 rounded-xl border border-primary/30 bg-primary/10 px-4 py-3 mb-4">
              <Flag className="w-5 h-5 text-primary shrink-0" />
              <span className="font-semibold text-foreground truncate">{scopeTitle}</span>
            </div>
          )}
          {scope === 'city' && scopeTitle && (
            <div className="flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 mb-4">
              <MapPin className="w-5 h-5 text-emerald-500 shrink-0" />
              <span className="font-semibold text-foreground truncate">{scopeTitle}</span>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
          ) : isError ? (
            <div className="glass-card rounded-xl p-8 text-center border border-destructive/30">
              <p className="text-muted-foreground mb-4">{t('playPublic.loadError')}</p>
              <button
                type="button"
                onClick={() => refetch()}
                className="btn-primary px-4 py-2 rounded-xl text-sm font-medium"
              >
                Retry
              </button>
            </div>
          ) : entries.length === 0 ? (
            <div className="glass-card rounded-xl p-8 text-center border border-muted/20">
              <Trophy className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-muted-foreground">{t('publicLeaderboard.noEntries')}</p>
            </div>
          ) : (
            <div className="space-y-2 sm:space-y-2.5">
              {entries.map((e, index) => {
                const medal = rankMedal(e.rank)
                const isTop3 = e.rank <= 3
                const colors = ['bg-primary', 'bg-destructive', 'bg-success', 'bg-warning', 'bg-chess', 'bg-primary', 'bg-destructive', 'bg-success']
                const color = colors[index % colors.length]
                const initial = (e.userName || '?').trim().charAt(0).toUpperCase()
                const locationLabel = scope === 'world' ? (e.countryCode || '').toUpperCase() : scope === 'country' ? (e.city ?? '') : ''
                return (
                  <div
                    key={e.publicParticipantId}
                    className={`rounded-lg sm:rounded-xl p-2.5 sm:p-3.5 lg:p-4 flex items-center gap-2 sm:gap-3 lg:gap-4 transition-all hover:bg-muted/30 ${
                      isTop3 ? 'glass-card border border-primary/10' : 'glass-card'
                    }`}
                  >
                    <span className="text-base sm:text-display-2 font-display font-bold w-6 sm:w-7 lg:w-8 text-center text-muted-foreground shrink-0">
                      {medal || e.rank}
                    </span>
                    <div className={`max-[479px]:hidden w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 rounded-full ${color} flex items-center justify-center text-[0.65rem] sm:text-small font-bold text-primary-foreground shrink-0`}>
                      {initial}
                    </div>
                    <div className="flex-1 min-w-0">
                      <button
                        type="button"
                        onClick={() => setProfileModalEntry(e)}
                        className="font-semibold text-small sm:text-nav truncate text-foreground hover:text-primary transition-colors text-left max-w-full"
                      >
                        {e.userName}
                      </button>
                      <div className="flex items-center flex-wrap gap-2 sm:gap-3 mt-0.5">
                        <span className="text-small sm:text-nav font-semibold text-primary">{formatPoints(e.totalPoints, t)}</span>
                        <span className="text-small text-muted-foreground/60">·</span>
                        <span className="text-small text-muted-foreground">{e.predictionCount} {t('roomPage.pred')}</span>
                        {locationLabel && (
                          <>
                            <span className="text-small text-muted-foreground/60">·</span>
                            <span className="text-small text-muted-foreground inline-flex items-center gap-1">
                              {scope === 'world' ? <Flag className="w-3 h-3" /> : <MapPin className="w-3 h-3" />}
                              {locationLabel}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-0.5 sm:gap-1 lg:gap-1.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => { setPlayerModalEntry(e); setPlayerModalViewType('games') }}
                        className="p-1.5 sm:px-2 sm:py-1.5 rounded-md sm:rounded-lg text-sky-400 text-small font-medium hover:opacity-80 transition-opacity flex items-center gap-1"
                        aria-label={t('roomPage.games')}
                      >
                        <Gamepad2 size={14} className="shrink-0" /> <span className="hidden sm:inline">{t('roomPage.games')}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => { setPlayerModalEntry(e); setPlayerModalViewType('stats') }}
                        className="p-1.5 sm:px-2 sm:py-1.5 rounded-md sm:rounded-lg text-violet-400 text-small font-medium hover:opacity-80 transition-opacity flex items-center gap-1"
                        aria-label={t('roomPage.stats')}
                      >
                        <BarChart3 size={14} className="shrink-0" /> <span className="hidden sm:inline">{t('roomPage.stats')}</span>
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
          {(selectedId || (allLeaguesSelected && effectiveIds.length > 0)) && (
            <>
              <PublicPlayerStatsGamesModal
                entry={playerModalEntry}
                tournamentId={selectedId ?? effectiveIds[0]}
                tournamentIds={effectiveIds}
                tournamentName={allLeaguesSelected ? t('publicFootball.allLeagues') : leagueDisplayName(leagues.find((l) => l.id === (selectedId ?? effectiveIds[0]))?.name ?? '')}
                viewType={playerModalViewType}
                isOpen={!!playerModalEntry}
                onClose={() => setPlayerModalEntry(null)}
                currentUserId={currentUserId}
              />
              <PublicProfileModal
                entry={profileModalEntry}
                tournamentIds={effectiveIds}
                isOpen={!!profileModalEntry}
                onClose={() => setProfileModalEntry(null)}
              />
            </>
          )}
        </>
      )}
    </div>
  )
}

export default PublicFootballPage
